import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Activity, ArrowLeft, MailCheck, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true); // never reveal whether the account exists
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7F9] px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#0047AB] flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-slate-900">CertIV Sims</span>
        </div>

        <Card className="border-slate-200 p-7" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <MailCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">Check your inbox</h1>
              <p className="text-sm text-slate-600 mt-2">
                If an account exists for <span className="font-semibold text-slate-900">{email}</span>,
                we've sent a link to reset your password. It expires in 30 minutes.
              </p>
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0047AB] mt-5">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-slate-900">Forgot your password?</h1>
              <p className="text-sm text-slate-600 mt-1.5">
                Enter your email and we'll send you a link to reset it.
              </p>
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="fp-email" className="text-xs">Email</Label>
                  <Input id="fp-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@clinic.com"
                    data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={busy} data-testid="forgot-submit-btn"
                  className="w-full bg-[#0047AB] hover:bg-[#003380] text-white">
                  {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Send reset link
                </Button>
              </form>
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0047AB] mt-5">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
